import React, { useState, useEffect, useCallback } from "react";
import { 
  Search, 
  RotateCcw, 
  Download, 
  Calendar as CalendarIcon, 
  Eye,
  X,
  FileSpreadsheet,
  FileText,
  Info,
  ChevronDown
} from "lucide-react";
import CustomDatePicker from "../../../customStyles/CustomDatePicker";
import SelectDropdown from "../../../customStyles/SelectDropdown";
import Pagination from "../../../helpers/Pagination"; 
import SummaryApi from "../../../common/SummaryApi";
import api from "../../../common/apiClient";

// Programmatic Export-க்கான லைப்ரரிகள்
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
const STATUS_OPTIONS = [
  { id: "present", label: "Present" },
  { id: "absent", label: "Absent" },
  { id: "on_leave", label: "On Leave" },
  { id: "half_day_leave", label: "Half Day Leave" },
  { id: "holiday", label: "Holiday" },
  { id: "week_off", label: "Week Off" }
];

const HRDetailedReportPage = () => {
  const getDefaultDates = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const todayStr = today.toISOString().split("T")[0];
    return {
      start: `${year}-${month}-01`,
      end: todayStr
    };
  };

  const defaultDates = getDefaultDates();

  const [search, setSearch] = useState("");
  const [selectedDept, setSelectedDept] = useState("");
  const [selectedDiv, setSelectedDiv] = useState("");
  const [selectedDesig, setSelectedDesig] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [startDate, setStartDate] = useState(defaultDates.start);
  const [endDate, setEndDate] = useState(defaultDates.end);
  
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10); 

  const [expandedUser, setExpandedUser] = useState(null);
  const [showExportMenu, setShowExportMenu] = useState(false);

  const [departments, setDepartments] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [designations, setDesignations] = useState([]);

  const [reportData, setReportData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [selectedPunchRecord, setSelectedPunchRecord] = useState(null);

  // ── 1. மெட்டாடேட்டாவைப் பெறுதல் ──
  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const deptUrl = SummaryApi.getAllDepartments?.url || "/api/departments";
        const deptMethod = SummaryApi.getAllDepartments?.method || "get";
        const deptRes = await api({ url: deptUrl, method: deptMethod });
        if (deptRes?.data?.success) {
          setDepartments(deptRes.data.data.map(d => ({ id: d._id, label: d.name })));
        }

        const divUrl = SummaryApi.getAllDivisions?.url || "/api/divisions";
        const divMethod = SummaryApi.getAllDivisions?.method || "get";
        const divRes = await api({ url: divUrl, method: divMethod });
        if (divRes?.data?.success) {
          setDivisions(divRes.data.data.map(v => ({ id: v._id, label: v.name })));
        }

        const desigUrl = SummaryApi.getAllDesignations?.url || "/api/designations";
        const desigMethod = SummaryApi.getAllDesignations?.method || "get";
        const desigRes = await api({ url: desigUrl, method: desigMethod });
        if (desigRes?.data?.success) {
          setDesignations(desigRes.data.data.map(s => ({ id: s._id, label: s.title })));
        }
      } catch (err) {
        console.error("Failed to load metadata:", err);
      }
    };
    fetchMetadata();
  }, []);

  // ── 2. அட்டெண்டன்ஸ் அறிக்கையைப் பெறுதல் ──
  const fetchReport = useCallback(async () => {
    setIsLoading(true);
    setErrorMsg("");
    try {
      const queryParams = new URLSearchParams({
        page,
        limit: limit === "All" ? 1000 : limit,
        ...(search && { search }),
        ...(selectedDept && { departmentId: selectedDept }),
        ...(selectedDiv && { divisionId: selectedDiv }),
        ...(selectedDesig && { designationId: selectedDesig }),
        ...(selectedStatus && { status: selectedStatus }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate })
      });

      const url = SummaryApi.getAllAttendaceReport?.url || "/api/attendance/hr/report";
      const method = SummaryApi.getAllAttendaceReport?.method || "get";
      
      const res = await api({ 
        url: `${url}?${queryParams.toString()}`, 
        method 
      });

      const resJson = res?.data || res;
      
      if (resJson?.success) {
        setReportData(resJson.data || []);
        setTotalRecords(resJson.total || 0);
      } else {
        setErrorMsg(resJson?.message || "Failed to fetch report data.");
      }
    } catch (err) {
      console.error("API error:", err);
      setErrorMsg("Connection failure. Unable to fetch reports.");
    } finally {
      setIsLoading(false);
    }
  }, [page, limit, search, selectedDept, selectedDiv, selectedDesig, selectedStatus, startDate, endDate]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleResetFilters = () => {
    setSearch("");
    setSelectedDept("");
    setSelectedDiv("");
    setSelectedDesig("");
    setSelectedStatus("");
    setStartDate(defaultDates.start);
    setEndDate(defaultDates.end);
    setPage(1);
  };

  const formatTime = (isoString) => {
    if (!isoString) return "--:--";
    return new Date(isoString).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "Asia/Kolkata"
    });
  };

  // ── 3. கேலெண்டர் கிரிட் வர்ணங்கள் ──
  const getGridColorClass = (status, missedCheckout, isLate) => {
    if (missedCheckout) return "bg-rose-100 border border-rose-300 text-rose-700 hover:bg-rose-200";
    if (isLate) return "bg-amber-100 border border-amber-300 text-amber-700 hover:bg-amber-200";
    
    switch (status) {
      case "present":
        return "bg-emerald-100 border border-emerald-200 text-emerald-700 hover:bg-emerald-250";
      case "absent":
        return "bg-rose-100 border border-rose-200 text-rose-700 hover:bg-rose-200";
      case "on_leave":
      case "half_day_leave":
        return "bg-sky-100 border border-sky-200 text-sky-700 hover:bg-sky-200";
      case "holiday":
        return "bg-indigo-100 border border-indigo-200 text-indigo-700 hover:bg-indigo-200";
      case "week_off":
        return "bg-slate-100 border border-slate-200 text-slate-500 hover:bg-slate-200";
      default:
        return "bg-slate-50 border border-slate-100 text-slate-400 hover:bg-slate-100";
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "present":
        return "bg-emerald-50 text-emerald-700 border border-emerald-200";
      case "absent":
        return "bg-rose-50 text-rose-700 border border-rose-200";
      case "on_leave":
      case "half_day_leave":
        return "bg-sky-50 text-sky-700 border border-sky-200";
      default:
        return "bg-slate-50 text-slate-600 border border-slate-200";
    }
  };

  // ── 4. கிரிட்டிற்கான மாதத்தின் 7-கிழமைகள் காலெண்டரைத் தயாரித்தல் (Real Calendar Days Grid) ──
  const getCalendarDaysGrid = (records) => {
    if (!records || records.length === 0) return [];
    
    // பில்டர் தேதியில் இருந்து வருடமும் மாதமும் எடுக்கிறோம்
    const activeDate = new Date(records[0].date);
    const year = activeDate.getFullYear();
    const month = activeDate.getMonth(); // 0 to 11

    const firstDayOfMonth = new Date(year, month, 1);
    const startDayOfWeek = firstDayOfMonth.getDay(); // 0: Sun, 1: Mon...
    const totalDaysInMonth = new Date(year, month + 1, 0).getDate();

    const gridCells = [];

    // 1. முதல் வாரத்திற்கான வெற்று இடங்கள் (Offset empty squares)
    for (let i = 0; i < startDayOfWeek; i++) {
      gridCells.push({ isPlaceholder: true, key: `empty-${i}` });
    }

    // 2. மாதத்தின் நாட்களை ஒவ்வொன்றாக ரெக்கார்டுகளுடன் இணைத்தல்
    for (let d = 1; d <= totalDaysInMonth; d++) {
      const currentCellDate = new Date(Date.UTC(year, month, d));
      const cellDateStr = currentCellDate.toISOString().split("T")[0];

      const matchedRecord = records.find(r => {
        const rDateStr = new Date(r.date).toISOString().split("T")[0];
        return rDateStr === cellDateStr;
      });

      gridCells.push({
        isPlaceholder: false,
        dayNum: d,
        date: currentCellDate,
        record: matchedRecord || null,
        key: `day-${d}`
      });
    }

    return gridCells;
  };

  // ── 5. புரோகிராமிங் ஏற்றுமதிகள் (Package Based Exports) ──
  
  // A. Excel Export using SheetJS (xlsx)
  const handleExportExcel = () => {
    const rows = [];
    reportData.forEach(emp => {
      emp.records.forEach(rec => {
        rows.push({
          "Date": new Date(rec.date).toLocaleDateString("en-IN"),
          "Employee ID": emp.user?.userId || "N/A",
          "Employee Name": `${emp.user?.firstName || ""} ${emp.user?.lastName || ""}`.trim(),
          "Department": emp.department?.name || "N/A",
          "Designation": emp.designation?.title || "N/A",
          "Status": rec.status || "N/A",
          "First In": rec.firstIn ? new Date(rec.firstIn).toLocaleTimeString() : "--",
          "Last Out": rec.lastOut ? new Date(rec.lastOut).toLocaleTimeString() : "--",
          "Total Punches": rec.punches ? rec.punches.length : 0,
          "Total Working Hours": rec.totalWorkingMinutes ? (rec.totalWorkingMinutes / 60).toFixed(2) : "0.00",
          "System Alerts": rec.adminNote || (rec.isLate ? `Late by ${rec.lateMinutes}m` : "")
        });
      });
    });

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Timesheets");
    
    // வொர்க்ஷீட் பத்திகளின் அகலத்தைச் சீரமைத்தல்
    worksheet["!cols"] = [
      { wch: 12 }, { wch: 12 }, { wch: 22 }, { wch: 18 }, { wch: 20 },
      { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 15 }, { wch: 18 }, { wch: 25 }
    ];

    XLSX.writeFile(workbook, `APD_Attendance_Report_${startDate}_to_${endDate}.xlsx`);
    setShowExportMenu(false);
  };

  // B. PDF Export using jsPDF & jspdf-autotable (No window.print screenshot!)
// B. PDF Export using jsPDF & jspdf-autotable (No window.print screenshot!)
  const handleExportPDF = () => {
    const doc = new jsPDF("landscape"); // Landscape layout is best for tables
    
    // Header setup with custom branding
    doc.setFillColor(11, 19, 43); // #0B132B Dark Blue
    doc.rect(0, 0, 297, 30, "F");

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("APD GROUP OF COMPANIES", 14, 13);
    
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(`Official Attendance Timesheet Report  |  Generated Range: ${startDate} to ${endDate}`, 14, 21);

    const tableRows = [];
    reportData.forEach(emp => {
      emp.records.forEach(rec => {
        tableRows.push([
          new Date(rec.date).toLocaleDateString("en-IN"),
          emp.user?.userId || "N/A",
          `${emp.user?.firstName || ""} ${emp.user?.lastName || ""}`.trim(),
          emp.department?.name || "N/A",
          rec.status ? rec.status.toUpperCase() : "N/A",
          rec.firstIn ? new Date(rec.firstIn).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "--",
          rec.lastOut ? new Date(rec.lastOut).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "--",
          rec.punches ? `${rec.punches.length} punches` : "0",
          rec.totalWorkingMinutes ? `${Math.floor(rec.totalWorkingMinutes / 60)}h ${rec.totalWorkingMinutes % 60}m` : "0h 0m",
          rec.adminNote || (rec.isLate ? `Late by ${rec.lateMinutes}m` : "--")
        ]);
      });
    });

    // doc.autoTable-க்கு பதிலாக autoTable(doc, ...) என மாற்றப்பட்டுள்ளது
    autoTable(doc, {
      startY: 36,
      head: [["Date", "Emp ID", "Employee Name", "Department", "Status", "First In", "Last Out", "Punches", "Work Hrs", "Alerts / Notes"]],
      body: tableRows,
      theme: "striped",
      headStyles: { fillColor: [11, 19, 43], textColor: [255, 255, 255], fontSize: 9, fontStyle: "bold" },
      bodyStyles: { fontSize: 8.5 },
      columnStyles: {
        0: { cellWidth: 22 },
        1: { cellWidth: 20 },
        2: { cellWidth: 35 },
        3: { cellWidth: 32 },
        4: { cellWidth: 20 },
        5: { cellWidth: 22 },
        6: { cellWidth: 22 },
        7: { cellWidth: 20 },
        8: { cellWidth: 22 }
      }
    });

    doc.save(`APD_Attendance_Report_${startDate}_to_${endDate}.pdf`);
    setShowExportMenu(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 p-4 md:p-6 font-sans">
      
      {/* ── பிரண்ட்-எண்ட் தலைப்பு மற்றும் ஏற்றுமதி பட்டன்கள் ── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <span className="p-2 rounded-lg bg-blue-50 text-blue-600">
              <CalendarIcon className="w-6 h-6" />
            </span>
            Detailed Attendance Report
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Analyzing timesheets for {totalRecords} employees matching filters.
          </p>
        </div>

        <div className="relative">
          <button 
            onClick={() => setShowExportMenu(!showExportMenu)}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-[#0B132B] hover:bg-[#1C2541] text-white transition-all rounded-lg text-sm font-semibold shadow-sm"
          >
            <Download size={16} />
            Export Options
            <ChevronDown size={14} />
          </button>

          {showExportMenu && (
            <div className="absolute right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg z-50 w-52 overflow-hidden animate-slide-up">
              <button
                onClick={handleExportExcel}
                className="w-full text-left px-4 py-2.5 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2 border-b border-slate-100"
              >
                <FileSpreadsheet size={15} className="text-emerald-600" />
                Export as CSV / Excel
              </button>
              <button
                onClick={handleExportPDF}
                className="w-full text-left px-4 py-2.5 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2"
              >
                <FileText size={15} className="text-rose-600" />
                Generate Document (PDF)
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── பில்டர்கள் பேனல் ── */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 md:p-5 mb-6 shadow-sm">
        <h2 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">
          Filters & Search Options
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Search Employee (Backend API search)</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="Name or Employee ID..."
                className="w-full pl-9 pr-3 h-10 rounded-md border border-slate-200 bg-white text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Start Date</label>
            <CustomDatePicker
              value={startDate}
              onChange={(date) => { setStartDate(date); setPage(1); }}
              placeholder="Select Start Date"
              showCapacityInfo={false}
              inputClassName="bg-white border-slate-200 text-slate-800 h-10 flex items-center justify-between"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">End Date</label>
            <CustomDatePicker
              value={endDate}
              onChange={(date) => { setEndDate(date); setPage(1); }}
              placeholder="Select End Date"
              showCapacityInfo={false}
              inputClassName="bg-white border-slate-200 text-slate-800 h-10 flex items-center justify-between"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
          <SelectDropdown
            label="Department"
            options={departments}
            value={selectedDept}
            onChange={(val) => { setSelectedDept(val); setPage(1); }}
            placeholder="All Departments"
            labelClassName="text-xs font-semibold text-slate-500"
            ChildClassName="bg-white border-slate-200 text-slate-800 h-10"
          />

          <SelectDropdown
            label="Division"
            options={divisions}
            value={selectedDiv}
            onChange={(val) => { setSelectedDiv(val); setPage(1); }}
            placeholder="All Divisions"
            labelClassName="text-xs font-semibold text-slate-500"
            ChildClassName="bg-white border-slate-200 text-slate-800 h-10"
          />

          <SelectDropdown
            label="Designation"
            options={designations}
            value={selectedDesig}
            onChange={(val) => { setSelectedDesig(val); setPage(1); }}
            placeholder="All Designations"
            labelClassName="text-xs font-semibold text-slate-500"
            ChildClassName="bg-white border-slate-200 text-slate-800 h-10"
          />

          <SelectDropdown
            label="Status"
            options={STATUS_OPTIONS}
            value={selectedStatus}
            onChange={(val) => { setSelectedStatus(val); setPage(1); }}
            placeholder="All Statuses"
            labelClassName="text-xs font-semibold text-slate-500"
            ChildClassName="bg-white border-slate-200 text-slate-800 h-10"
          />
        </div>

        <div className="flex justify-end border-t border-slate-100 pt-3">
          <button
            onClick={handleResetFilters}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-500 hover:text-[#0B132B] bg-slate-100 hover:bg-slate-200 transition-all rounded-md"
          >
            <RotateCcw size={13} />
            Reset All Filters
          </button>
        </div>
      </div>

      {/* ── அட்டெண்டன்ஸ் கார்டு பட்டியல் ── */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3 bg-white border rounded-xl shadow-sm">
            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-slate-500 text-sm">Loading attendance timesheets...</p>
          </div>
        ) : errorMsg ? (
          <div className="py-16 text-center text-rose-500 bg-white border rounded-xl shadow-sm px-4">
            <p className="font-semibold">{errorMsg}</p>
          </div>
        ) : reportData.length === 0 ? (
          <div className="py-20 text-center text-slate-400 bg-white border rounded-xl shadow-sm">
            <p className="text-lg font-medium">No reports matched your criteria.</p>
          </div>
        ) : (
          reportData.map((group) => {
            const isExpanded = expandedUser === group.user?._id;

            // மாதாந்திர சுருக்க கணக்கீடு
            let presentCount = 0;
            let absentCount = 0;
            let lateCount = 0;
            let totalHours = 0;

            group.records.forEach(r => {
              if (r.status === "present") presentCount++;
              if (r.status === "absent") absentCount++;
              if (r.isLate) lateCount++;
              totalHours += r.totalWorkingMinutes ? r.totalWorkingMinutes / 60 : 0;
            });

            // 7-கிழமைகள் காலெண்டர் கிரிட்டை உருவாக்குகிறோம்
            const calendarCells = getCalendarDaysGrid(group.records);

            return (
              <div 
                key={group.user?._id} 
                className="border border-slate-200 rounded-xl bg-white overflow-hidden shadow-sm transition-all"
              >
                {/* ── கார்டின் மேல் பகுதி (Employee Header Panel) ── */}
                <div 
                  onClick={() => setExpandedUser(isExpanded ? null : group.user?._id)}
                  className="p-4 flex flex-col lg:flex-row lg:items-center justify-between gap-4 cursor-pointer hover:bg-slate-50 hover:border-l-4 hover:border-[#0B132B] transition-all"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full bg-blue-50 text-[#0B132B] flex items-center justify-center font-bold text-sm border border-blue-100">
                      {group.user?.firstName?.charAt(0)}{group.user?.lastName?.charAt(0)}
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 text-base">
                        {group.user?.firstName} {group.user?.lastName}
                      </h3>
                      <p className="text-xs text-slate-500">
                        ID: <span className="text-blue-600 font-bold">{group.user?.userId}</span> • {group.designation?.title || "Employee"} • {group.department?.name || "N/A"}
                      </p>
                    </div>
                  </div>

                  {/* மாதாந்திர சுருக்கம் */}
                  <div className="flex flex-wrap items-center gap-3 md:gap-6 border-t lg:border-t-0 border-slate-100 pt-3 lg:pt-0 text-xs">
                    <div className="text-center bg-slate-50 px-2.5 py-1 rounded-md">
                      <span className="text-slate-400 block text-[10px] uppercase font-bold">Present</span>
                      <span className="font-bold text-emerald-600">{presentCount} Days</span>
                    </div>
                    <div className="text-center bg-slate-50 px-2.5 py-1 rounded-md">
                      <span className="text-slate-400 block text-[10px] uppercase font-bold">Absent</span>
                      <span className="font-bold text-rose-600">{absentCount} Days</span>
                    </div>
                    <div className="text-center bg-slate-50 px-2.5 py-1 rounded-md">
                      <span className="text-slate-400 block text-[10px] uppercase font-bold">Late Logs</span>
                      <span className="font-bold text-amber-600">{lateCount} Days</span>
                    </div>
                    <div className="text-center bg-slate-50 px-2.5 py-1 rounded-md">
                      <span className="text-slate-400 block text-[10px] uppercase font-bold">Work Hrs</span>
                      <span className="font-bold text-slate-800">{totalHours.toFixed(1)} hrs</span>
                    </div>
                    <span className="text-blue-600 font-semibold hover:underline flex items-center gap-1">
                      {isExpanded ? "Hide Details ▲" : "View Details / Grid ▼"}
                    </span>
                  </div>
                </div>

                {/* ── 5. புதிய கஸ்டமைஸ் செய்யப்பட்ட 7-கிழமைகள் காலெண்டர் கிரிட் (7-Column Calendar Grid) ── */}
                <div className="px-4 pb-4 border-t border-slate-100 pt-3 bg-slate-50/10">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-2 flex items-center gap-1">
                    Monthly Calendar Sheet
                  </p>

                  <div className="max-w-xs md:max-w-md">
                    {/* கிழமைகள் தலைப்பு (Sunday - Saturday) */}
                    <div className="grid grid-cols-7 gap-1 text-center mb-1 text-[10px] font-bold text-slate-400">
                      <div>Sun</div>
                      <div>Mon</div>
                      <div>Tue</div>
                      <div>Wed</div>
                      <div>Thu</div>
                      <div>Fri</div>
                      <div>Sat</div>
                    </div>

                    {/* காலெண்டர் கட்டங்கள் (7-Column Grid Cells) */}
                    <div className="grid grid-cols-7 gap-1">
                      {calendarCells.map((cell, idx) => {
                        if (cell.isPlaceholder) {
                          return <div key={cell.key} className="w-8 h-8 md:w-10 md:h-10 bg-transparent" />;
                        }

                        const rec = cell.record;
                        const isMissed = rec?.missedCheckout || false;
                        const isLate = rec?.isLate || false;
                        const status = rec?.status || "inactive";

                        const colorStyle = getGridColorClass(status, isMissed, isLate);

                        return (
                          <div
                            key={cell.key}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (rec) {
                                setSelectedPunchRecord({ ...rec, user: group.user });
                              }
                            }}
                            className={`
                              w-8 h-8 md:w-10 md:h-10 rounded-md flex flex-col items-center justify-center text-[10px] font-bold shadow-sm transition-all
                              ${rec ? "cursor-pointer" : "opacity-30 cursor-not-allowed"}
                              ${colorStyle}
                            `}
                            title={rec ? `Date: ${cell.dayNum} | Status: ${status}` : "No Record"}
                          >
                            <span>{cell.dayNum}</span>
                            {rec && (
                              <span className="text-[7px] md:text-[8px] font-semibold uppercase tracking-tighter opacity-85">
                                {status === "on_leave" || status === "half_day_leave" ? "Lve" : status.substring(0, 3)}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* ── விரிவடைந்த அட்டவணைப் பகுதி ── */}
                {isExpanded && (
                  <div className="border-t border-slate-200 bg-white overflow-x-auto">
                    <table className="w-full text-left border-collapse text-xs">
                      <thead>
                        <tr className="bg-slate-50 text-slate-500 font-bold border-b border-slate-200 uppercase">
                          <th className="py-2.5 px-4">Date</th>
                          <th className="py-2.5 px-4">Shift Name</th>
                          <th className="py-2.5 px-4">Status</th>
                          <th className="py-2.5 px-4 text-center">First In</th>
                          <th className="py-2.5 px-4 text-center">Last Out</th>
                          <th className="py-2.5 px-4 text-center">Checkout Logs</th>
                          <th className="py-2.5 px-4 text-center">Hours Worked</th>
                          <th className="py-2.5 px-4">Notes / Alerts</th>
                          <th className="py-2.5 px-4 text-right">Punches</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 text-slate-600">
                        {group.records.map((rec) => (
                          <tr key={rec._id} className="hover:bg-slate-50/50 transition-colors">
                            <td className="py-2.5 px-4 font-semibold text-slate-900 whitespace-nowrap">
                              {new Date(rec.date).toLocaleDateString("en-IN", { day: '2-digit', month: 'short', year: 'numeric' })}
                            </td>
                            <td className="py-2.5 px-4 text-slate-500">
                              {group.shift?.name || "General Shift"} ({group.shift?.startTime} - {group.shift?.endTime})
                            </td>
                            <td className="py-2.5 px-4">
                              <span className={`inline-flex px-2 py-0.5 rounded font-semibold ${getStatusBadge(rec.status)}`}>
                                {rec.status}
                              </span>
                            </td>
                            <td className="py-2.5 px-4 text-center font-medium text-slate-800">{formatTime(rec.firstIn)}</td>
                            <td className="py-2.5 px-4 text-center font-medium text-slate-800">{formatTime(rec.lastOut)}</td>
                            <td className="py-2.5 px-4 text-center font-semibold text-slate-700">
                              {rec.punches ? `${rec.punches.length} punches` : "0"}
                            </td>
                            <td className="py-2.5 px-4 text-center font-bold text-slate-900">
                              {rec.totalWorkingMinutes ? `${Math.floor(rec.totalWorkingMinutes / 60)}h ${rec.totalWorkingMinutes % 60}m` : "0m"}
                            </td>
                            <td className="py-2.5 px-4">
                              <div className="flex flex-col gap-1 items-start">
                                {rec.isLate && (
                                  <span className="text-[10px] bg-rose-50 text-rose-600 border border-rose-200 px-1.5 py-0.5 rounded font-semibold">
                                    Late: {rec.lateMinutes} mins
                                  </span>
                                )}
                                {rec.adminNote && (
                                  <span className="text-[10px] bg-rose-50 text-rose-600 border border-rose-200 px-1.5 py-0.5 rounded font-medium">
                                    <Info size={10} className="inline mr-0.5" />
                                    {rec.adminNote}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="py-2.5 px-4 text-right">
                              <button
                                onClick={() => setSelectedPunchRecord({ ...rec, user: group.user })}
                                className="p-1 hover:bg-slate-200 rounded text-slate-400 hover:text-slate-700 transition-all"
                              >
                                <Eye size={15} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* ── 6. Pagination காம்போனென்ட் ── */}
      {!isLoading && reportData.length > 0 && (
        <Pagination
          totalItems={totalRecords}
          itemsPerPage={limit}
          currentPage={page}
          onPageChange={(p) => setPage(p)}
          onItemsPerPageChange={(l) => { setLimit(l); setPage(1); }}
        />
      )}

      {/* ── பஞ்ச் லாக்ஸ் மாடல் ── */}
      {selectedPunchRecord && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setSelectedPunchRecord(null)} />
          <div className="relative bg-white border border-slate-200 rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-slide-up">
            <div className="bg-slate-50 p-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-slate-900">Daily Punch Logs</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  {new Date(selectedPunchRecord.date).toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "long", year: "numeric" })}
                </p>
              </div>
              <button onClick={() => setSelectedPunchRecord(null)} className="p-1 hover:bg-slate-200 rounded text-slate-400 hover:text-slate-700 transition-all">
                <X size={18} />
              </button>
            </div>

            <div className="p-4 max-h-[350px] overflow-y-auto space-y-4">
              <div className="flex items-center gap-3 pb-3 border-b border-slate-200">
                <div className="w-10 h-10 rounded-full bg-blue-50 text-[#0B132B] flex items-center justify-center font-bold text-sm border border-blue-100">
                  {selectedPunchRecord.user?.firstName?.charAt(0)}{selectedPunchRecord.user?.lastName?.charAt(0)}
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900">{selectedPunchRecord.user?.firstName} {selectedPunchRecord.user?.lastName}</h4>
                  <p className="text-xs text-slate-500">{selectedPunchRecord.user?.userId || "N/A"}</p>
                </div>
              </div>

              {selectedPunchRecord.punches && selectedPunchRecord.punches.length > 0 ? (
                <div className="relative pl-6 space-y-4 border-l border-slate-200">
                  {selectedPunchRecord.punches.map((punch, idx) => {
                    const isCheckIn = punch.type === "in";
                    return (
                      <div key={idx} className="relative">
                        <span className={`absolute -left-[31px] top-1.5 w-2.5 h-2.5 rounded-full ring-4 ring-white ${isCheckIn ? "bg-emerald-500" : "bg-rose-500"}`} />
                        <div>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs font-bold uppercase ${isCheckIn ? "text-emerald-500" : "text-rose-500"}`}>Check-{punch.type}</span>
                            <span className="text-slate-800 font-semibold text-sm">{formatTime(punch.time)}</span>
                          </div>
                          {punch.location?.address && (
                            <p className="text-slate-500 text-[11px] mt-0.5 leading-relaxed">{punch.location.address}</p>
                          )}
                          <div className="flex gap-2 mt-1 text-[10px] text-slate-400">
                            {punch.method && <span className="px-1.5 py-0.5 bg-slate-50 rounded">Method: {punch.method}</span>}
                            {punch.ipAddress && <span className="px-1.5 py-0.5 bg-slate-50 rounded">IP: {punch.ipAddress}</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="py-6 text-center text-slate-400 text-sm">No punch logs recorded for this day.</div>
              )}
            </div>

            <div className="bg-slate-50 p-3 border-t border-slate-200 flex justify-end">
              <button onClick={() => setSelectedPunchRecord(null)} className="px-4 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg text-xs font-semibold">
                Close Logs
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HRDetailedReportPage;